import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors, defaultDropAnimationSideEffects } from '@dnd-kit/core';
import { projectsAPI, tasksAPI, sprintsAPI, customersAPI } from '../services/api';
import KanbanColumn from '../components/KanbanColumn';
import TaskCard from '../components/TaskCard';
import TaskEditDialog from '../components/TaskEditDialog';
import BacklogTable from '../components/BacklogTable';

function BoardPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [boardData, setBoardData] = useState(null);
  const [sprints, setSprints] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState('all');
  const [showAllDone, setShowAllDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Filter projects when customer changes
  useEffect(() => {
    if (selectedCustomer) {
      const filtered = allProjects.filter(p => p.customer_id === parseInt(selectedCustomer));
      setProjects(filtered);
      setSelectedProject('');
    } else {
      setProjects(allProjects);
    }
  }, [selectedCustomer, allProjects]);

  // Load board data when project changes
  useEffect(() => {
    if (selectedProject) {
      loadBoardData();
      loadSprints();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, selectedSprint, showAllDone]);

  const loadProjects = async () => {
    try {
      const [customersRes, projectsRes] = await Promise.all([
        customersAPI.getAll(),
        projectsAPI.getAll(),
      ]);
      setCustomers(customersRes.data);
      setAllProjects(projectsRes.data);
      setProjects(projectsRes.data);
      if (projectsRes.data.length > 0) {
        setSelectedProject(projectsRes.data[0].id);
      }
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    }
  };

  const loadBoardData = async () => {
    if (!selectedProject) return;

    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (selectedSprint && selectedSprint !== 'all') {
        params.sprint_id = selectedSprint;
      }
      if (showAllDone) {
        params.show_all_done = true;
      }

      const response = await tasksAPI.getBoardView(selectedProject, params);
      setBoardData(response.data);
    } catch (err) {
      setError('Fehler beim Laden des Boards');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSprints = async () => {
    try {
      const response = await sprintsAPI.getAll(selectedProject, 'active');
      setSprints(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Sprints', err);
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const taskId = active.id.toString().startsWith('backlog-')
      ? parseInt(active.id.toString().replace('backlog-', ''))
      : active.id;
    const task = findTaskById(taskId) || findBacklogTaskById(taskId);
    setActiveTask(task);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveTask(null);
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    // Check if dragging from backlog
    const isFromBacklog = activeId.toString().startsWith('backlog-');
    const taskId = isFromBacklog ? parseInt(activeId.toString().replace('backlog-', '')) : activeId;

    // Find the task
    const task = isFromBacklog ? findBacklogTaskById(taskId) : findTaskById(taskId);
    // Check if overId is a status first, then check if it's a task
    const targetStatus = findStatusById(overId) || findStatusByTaskId(overId);

    if (!task || !targetStatus) {
      setActiveTask(null);
      return;
    }

    // If moving from backlog to board
    if (isFromBacklog) {
      // Backlog tasks can only be moved to "Zu erledigen" (order=1) or "In Bearbeitung" (order=2)
      if (targetStatus.order > 2) {
        setError('Backlog-Tasks können nur in "Zu erledigen" oder "In Bearbeitung" verschoben werden');
        setTimeout(() => setError(null), 3000);
        setActiveTask(null);
        return;
      }

      const targetTasks = findTasksByStatusId(targetStatus.id);
      const newPosition = overId === targetStatus.id ? targetTasks.length : targetTasks.findIndex(t => t.id === overId);
      await moveTask(task, targetStatus.id, newPosition);
    } else {
      // If dropped on same status, handle reordering
      if (task.status_id === targetStatus.id) {
        const statusTasks = findTasksByStatusId(targetStatus.id);
        const oldIndex = statusTasks.findIndex(t => t.id === taskId);
        const newIndex = statusTasks.findIndex(t => t.id === overId);

        if (oldIndex !== newIndex && newIndex !== -1) {
          await moveTask(task, targetStatus.id, newIndex);
        }
      } else {
        // Moved to different status
        const targetTasks = findTasksByStatusId(targetStatus.id);
        const newPosition = overId === targetStatus.id ? targetTasks.length : targetTasks.findIndex(t => t.id === overId);
        await moveTask(task, targetStatus.id, newPosition);
      }
    }

    setActiveTask(null);
  };

  const moveTask = async (task, newStatusId, newPosition) => {
    try {
      await tasksAPI.move(task.id, newStatusId, newPosition);
      await loadBoardData(); // Reload board data
    } catch (err) {
      setError('Fehler beim Verschieben der Aufgabe');
      console.error('Move task error:', err);
    }
  };

  const findTaskById = (id) => {
    if (!boardData) return null;
    for (const column of boardData.board) {
      const task = column.tasks.find(t => t.id === id);
      if (task) return task;
    }
    return null;
  };

  const findBacklogTaskById = (id) => {
    if (!boardData || !boardData.backlog) return null;
    return boardData.backlog.find(t => t.id === id);
  };

  const findStatusByTaskId = (taskId) => {
    if (!boardData) return null;
    for (const column of boardData.board) {
      if (column.tasks.some(t => t.id === taskId)) {
        return column.status;
      }
    }
    return null;
  };

  const findStatusById = (statusId) => {
    if (!boardData) return null;
    const column = boardData.board.find(col => col.status.id === statusId);
    return column ? column.status : null;
  };

  const findTasksByStatusId = (statusId) => {
    if (!boardData) return [];
    const column = boardData.board.find(col => col.status.id === statusId);
    return column ? column.tasks : [];
  };

  const handleTaskClick = (task) => {
    setEditTask(task);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditTask(null);
  };

  const handleTaskUpdated = () => {
    loadBoardData();
  };

  const dropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.3',
        },
      },
    }),
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          Board
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Kunde</InputLabel>
            <Select
              value={selectedCustomer}
              label="Kunde"
              onChange={(e) => setSelectedCustomer(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: { maxHeight: 300 }
                }
              }}
              sx={{
                '& .MuiSelect-select': {
                  minWidth: '200px'
                }
              }}
            >
              <MenuItem value="">Alle Kunden</MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Projekt</InputLabel>
            <Select
              value={selectedProject}
              label="Projekt"
              onChange={(e) => setSelectedProject(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: { maxHeight: 300 }
                }
              }}
              sx={{
                '& .MuiSelect-select': {
                  minWidth: '200px'
                }
              }}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Sprint</InputLabel>
            <Select
              value={selectedSprint}
              label="Sprint"
              onChange={(e) => { setShowAllDone(false); setSelectedSprint(e.target.value); }}
            >
              <MenuItem value="all">Alle Tasks</MenuItem>
              {sprints.map((sprint) => (
                <MenuItem key={sprint.id} value={sprint.id}>
                  {sprint.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/tasks')}
            sx={{
              background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
              },
            }}
          >
            Task erstellen
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Board */}
      {!loading && boardData && boardData.board && boardData.board.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 2,
              height: '70vh',
              alignItems: 'stretch',
            }}
          >
            {boardData.board.map((column) => (
              <KanbanColumn
                key={column.status.id}
                status={column.status}
                tasks={column.tasks}
                onTaskClick={handleTaskClick}
                hiddenDoneCount={column.hidden_done_count || 0}
                onShowAllDone={() => setShowAllDone(true)}
              />
            ))}
          </Box>

          {/* Backlog Section */}
          {boardData.backlog && boardData.backlog.length > 0 && (
            <BacklogTable
              tasks={boardData.backlog}
              onTaskClick={handleTaskClick}
            />
          )}

          <DragOverlay dropAnimation={dropAnimation}>
            {activeTask ? (
              <Box sx={{
                transform: 'rotate(5deg)',
                opacity: 0.9,
                width: '300px',
                maxWidth: '300px',
                cursor: 'grabbing'
              }}>
                <TaskCard task={activeTask} isDragging />
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Task Edit Dialog */}
      <TaskEditDialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        task={editTask}
        projectId={selectedProject}
        onTaskUpdated={handleTaskUpdated}
      />

      {/* Empty State */}
      {!loading && boardData && (!boardData.board || boardData.board.length === 0) && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Keine Tasks für dieses Projekt vorhanden
          </Typography>
        </Paper>
      )}

      {/* No Project Selected */}
      {!loading && !boardData && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Wählen Sie ein Projekt aus, um das Board anzuzeigen
          </Typography>
        </Paper>
      )}
    </Container>
  );
}

export default BoardPage;
